import { changeEmailSender, resolveEmailSender } from "./emailSenderPolicy";
import { StyledSelect } from "../../primitives/styled-select";
import * as React from "react";
import { Mail } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { cn } from "../../lib/cn";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
  LoadPeopleOptions,
  PeopleSearchHit,
} from "../ActionFieldEditor";
import {
  type AttachmentChipValue,
  EmailAttachmentField,
  type SearchAttachableDocuments,
  type UploadAttachment,
  isAttachmentArray,
} from "../email/EmailAttachmentField";
import {
  EmailComposeSheet,
  type EmailContactsQuery,
} from "../email/EmailComposeSheet";
import { emailBodyToHtml } from "../email/emailBodyHtml";
import {
  EmailSignatureFooter,
  type EmailSignatureOption,
} from "../email/EmailSignatureFooter";
import type { RecipientChipsSuggestion } from "../email/RecipientChips";
import type { RichEmailAiEdit } from "../email/RichEmailBody";
import {
  ENVELOPE_CLASSES,
  EnvelopeHeader,
  asRecipientText,
  asString,
  asStringArray,
  specOfName,
  useEnvelopeCommit,
} from "./envelope-primitives";
import type {
  RefineSubmitInput,
  RefineSubmitResult,
  RefineSubmitWithDiff,
} from "./InlineAIRefiner";

/**
 * EmailSendDraftPreview — envelope-style HITL editor for ``send_email`` /
 * ``email_send_draft`` proposals (May 26, 2026 rewrite).
 *
 * Previously a read-only mock that rendered *below* a stacked
 * ``ActionFieldEditor`` form — operators saw the same fields twice (form
 * on top, mock at the bottom) and complained the layout was unusable.
 *
 * Now: ONE envelope. The labels stay on the left ("Da:", "A:", …) and the
 * input controls sit inline next to them, exactly like Gmail's compose
 * pane. When the parent passes a controlled API (``values`` + ``onChange``
 * + ``schema``) we render real inputs; without them we degrade to a
 * read-only display (post-execution snapshot).
 *
 * Field ownership: the parent (``ActionDrawer``) is told via
 * ``previewOwnedFields`` to skip ``from_account / to / cc / bcc / subject
 * / body`` in its stacked editor — those fields live HERE. Any other
 * action-time field (e.g. a future ``send_at`` scheduler) still renders
 * in the stacked editor below the preview.
 */

// ---------------------------------------------------------------------------
// Unified "letter" sheet — ONE bordered sheet with hairline-separated rows and
// a single shared focus-ring (mirrors ``ReplyComposeSection`` in the
// Conversazioni composer). Orange is reserved for the primary CTA; every field
// is hairlines + the ink text ladder. Jul 2026 restyle.
// ---------------------------------------------------------------------------

const FIELD_LABEL =
  "w-8 shrink-0 select-none font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]";
const SHEET_CONTROL =
  "flex-1 bg-transparent text-[12.5px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";
const SHEET_HELP = "px-3 pb-1.5 text-[11px] text-ink-3";
const SHEET_ERR = "px-3 pb-1.5 text-[11px] text-red-600";

/** MS Graph / Google People fanout hit → RecipientChips autocomplete shape. */
function toChipSuggestions(
  hits: readonly PeopleSearchHit[],
): RecipientChipsSuggestion[] {
  return hits.map((h) => ({
    id: h.email,
    display: h.display_name || h.email,
    email: h.email,
    kind: h.provider,
  }));
}


export const EMAIL_PREVIEW_OWNED_FIELDS = [
  "from_account",
  "to",
  "cc",
  "bcc",
  "subject",
  "body",
  // Owned so the stacked ``ActionFieldEditor`` never double-renders the rich
  // body's HTML twin IF the send action's schema ever ships it (the FE commits
  // ``body_html`` alongside ``body`` only when the schema declares the field).
  "body_html",
  // Owned so the stacked ``ActionFieldEditor`` never renders the signature
  // include/hide flag as a raw boolean field — the envelope surfaces it as the
  // Inclusa/Nascosta Eye toggle inside the compose sheet's signature footer.
  "include_signature",
] as const;

/**
 * Fields the email envelope OWNS so the stacked ``ActionFieldEditor`` skips
 * them. The attachment field's NAME is schema-driven (canonically
 * ``attachments``, but read dynamically off the ``type: "file"`` spec so a
 * renamed field still gets suppressed) — the envelope now renders it INSIDE
 * the compose sheet (same ``EmailAttachmentField`` as the inbox reply), so the
 * stacked editor must no longer double-render it as a separate "Allegati"
 * section below. Hosts pass this (computed from the live schema) as the
 * ``previewOwnedFields`` for every email action instead of the static list.
 */
export function emailOwnedFields(
  schema: readonly ActionFieldSpec[] | undefined,
): string[] {
  const fileSpec = schema?.find((s) => s.type === "file");
  return [...EMAIL_PREVIEW_OWNED_FIELDS, fileSpec?.name].filter(
    (name): name is string => Boolean(name),
  );
}

/** Coerce a schema field value to the controlled attachment array shape. */
function asAttachmentArray(value: unknown): AttachmentChipValue[] {
  return isAttachmentArray(value) ? value : [];
}

type EmailFieldName = (typeof EMAIL_PREVIEW_OWNED_FIELDS)[number];

export interface EmailSendDraftPreviewProps {
  /** Proposal id — required for the inline AI refiner (refine-field BE). */
  proposalId?: string;
  /**
   * Host-supplied transport for the inline AI refiner. When omitted, the
   * envelope renders the body as a plain textarea — selection bubble is
   * disabled. ``apps/client`` builds a single shared submitter wired to
   * ``@ando/core.refineField`` and threads it into every editable preview.
   */
  refineSubmit?: (input: RefineSubmitInput) => Promise<RefineSubmitResult>;
  /**
   * OPTIONAL diff-mode transport (Jul 2026). When supplied, the body's
   * ``InlineAIRefiner`` reviews the rewrite as an Apple-style inline redline
   * (accept/reject each change) before committing, instead of replacing the
   * selection in place. Takes precedence over ``refineSubmit`` when both are
   * present; ``refineSubmit`` stays wired as the fallback.
   */
  refineSubmitWithDiff?: RefineSubmitWithDiff;
  /** Live field values (proposed merged with edits). */
  proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** When provided (post-execution), draws the executed copy. */
  executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled API — present when the proposal is still editable. */
  schema?: readonly ActionFieldSpec[];
  values?: Readonly<Record<string, ActionFieldValue>>;
  errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  onChange?: (next: Record<string, ActionFieldValue>) => void;
  disabled?: boolean;
  /**
   * The caller's EFFECTIVE email signature (user override → org Brand Kit),
   * already sanitized by the host. Rendered read-only under the body so the
   * operator SEES what the BE appends at send time (Jul 2026 — signatures
   * were invisible in the form).
   */
  signatureHtml?: string | null;
  /**
   * Optional POOL of selectable signatures (multi-signature model, Jul 2026).
   * When >1 is supplied, the signature footer renders a compact dropdown so the
   * operator can pick ANY pool signature for this email. Data (incl. the
   * resolved ``activeSignatureId`` and the ``onPickSignature`` callback) comes
   * from the host mount (``useSignatures`` + ``useEffectiveSignature``).
   */
  signatures?: readonly EmailSignatureOption[];
  /** Id of the currently-active pool signature (``null`` = global default). */
  activeSignatureId?: string | null;
  /** Fired when the operator picks a different pool signature. */
  onPickSignature?: (id: string) => void;
  /**
   * People-search transport (Google People + MS Graph fanout). Wired by
   * the host (``apps/client`` ScalarActionProposalMount) so the envelope
   * ``A`` / ``Cc`` / ``Ccn`` rows render the SAME chip-with-autocomplete
   * UX as the generic ``ActionFieldEditor`` ``recipients`` renderer.
   * Both surfaces delegate to ``<RecipientsInput>`` — single DRY anchor.
   * When omitted (Storybook / read-only mounts) the rows degrade to
   * free-text chips.
   */
  loadPeopleOptions?: LoadPeopleOptions;
  /**
   * Upload transport for staged attachments (device files). When provided AND
   * the schema declares a ``type: "file"`` field, the envelope renders the
   * shared ``<EmailAttachmentField>`` INSIDE the compose sheet — same place +
   * pencil style as the inbox reply. When omitted the attachment field stays
   * with the stacked ``ActionFieldEditor`` (legacy fallback).
   */
  uploadAttachment?: UploadAttachment;
  /**
   * Search transport for the "Scegli dai documenti Ando" attachment picker.
   * The send-email BE resolves ``content_item_id`` chips at send time, so this
   * surface KEEPS the picker (unlike the inbox reply, whose BE can't resolve
   * it). Threaded straight into ``<EmailAttachmentField>``.
   */
  searchAttachableDocuments?: SearchAttachableDocuments;
  /**
   * Active Brand Kit fonts — surfaced in the body editor's font dropdown above
   * the built-in generics. ``@ando/ui`` can't call app hooks, so the host mount
   * (``FocusedActionView`` / ``ScalarActionProposalMount``) resolves these via
   * ``useBrandFonts`` and threads them in. Absent → only the built-ins.
   */
  brandFonts?: readonly { family: string; fallback: string }[];
  className?: string;
}

// Field constants + ``asString`` / ``asRecipientText`` / ``EnvelopeRow`` /
// ``useEnvelopeCommit`` / ``specOfName`` live in ``./envelope-primitives``
// so the matching ``SendMessagePreview`` (and any future PEC / WhatsApp /
// Salesforce-note envelope) consumes the same building blocks. May 26,
// 2026 DRY consolidation — see envelope-primitives.tsx header for rationale.

// ---------------------------------------------------------------------------
// EmailSendDraftPreview
// ---------------------------------------------------------------------------

export const EmailSendDraftPreview: React.FC<EmailSendDraftPreviewProps> = ({
  proposalId,
  refineSubmit,
  refineSubmitWithDiff,
  proposedFields,
  executedFields,
  schema,
  values,
  errors,
  onChange,
  disabled,
  loadPeopleOptions,
  uploadAttachment,
  searchAttachableDocuments,
  brandFonts,
  className,
  signatureHtml,
  signatures,
  activeSignatureId,
  onPickSignature,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  // Display fallback when uncontrolled (post-execution snapshot, or before
  // the lazy schema fetch resolves) — keeps the legacy read-only behaviour.
  const display = executedFields ?? proposedFields;
  const liveValues = values ?? display;

  const fromSpec = specOfName(schema, "from_account");
  const toSpec = specOfName(schema, "to");
  const ccSpec = specOfName(schema, "cc");
  const bccSpec = specOfName(schema, "bcc");
  const subjectSpec = specOfName(schema, "subject");
  const bodySpec = specOfName(schema, "body");

  const commit = useEnvelopeCommit<EmailFieldName>(values, onChange);

  // ---------------------------------------------------------------------
  // Read-only fallback — pre-1.4.0 envelope mock layout (used when the
  // proposal is in a non-editable state such as ``executed`` / ``failed``,
  // and when the parent didn't pass a controlled API).
  // ---------------------------------------------------------------------
  if (!isControlled) {
    const to = asRecipientText(display.to);
    const cc = asRecipientText(display.cc);
    const bcc = asRecipientText(display.bcc);
    const fromAccount = asString(display.from_account);
    const displaySender = resolveEmailSender(fromSpec?.options ?? [], fromAccount);
    const fromLabel = displaySender.option?.label ?? displaySender.realEmail ?? i18n._(msg`Account email`);
    const subject = asString(display.subject);
    const body = asString(display.body);

    return (
      <div className={cn("flex flex-col gap-2 text-sm", className)}>
        <EnvelopeHeader
          icon={<Mail className="h-3.5 w-3.5" />}
          label={i18n._(msg`Bozza email`)}
        />
        <div className={ENVELOPE_CLASSES.cardReadonly}>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            {fromAccount ? (
              <>
                <dt className="text-ink-3">{i18n._(msg`Da:`)}</dt>
                <dd className="truncate text-ink-1">{fromLabel}</dd>
              </>
            ) : null}
            <dt className="text-ink-3">{i18n._(msg`A:`)}</dt>
            <dd className="truncate text-ink-1">{to || "—"}</dd>
            {cc ? (
              <>
                <dt className="text-ink-3">{i18n._(msg`Cc:`)}</dt>
                <dd className="truncate text-ink-1">{cc}</dd>
              </>
            ) : null}
            {bcc ? (
              <>
                <dt className="text-ink-3">{i18n._(msg`Ccn:`)}</dt>
                <dd className="truncate text-ink-1">{bcc}</dd>
              </>
            ) : null}
            <dt className="text-ink-3">{i18n._(msg`Oggetto:`)}</dt>
            <dd className="truncate font-medium text-ink-1">{subject || "—"}</dd>
          </dl>
          <div className="mt-2 whitespace-pre-wrap border-hairline border-t pt-2 text-ink-1 text-xs leading-relaxed">
            {body || (
              <span className="text-ink-3 italic">
                {i18n._(msg`Corpo email vuoto.`)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Controlled — the shared ``EmailComposeSheet`` (same rich compose as the
  // inbox reply): Tiptap toolbar, selection AI-edit (⌘K / ✨), ghost-text
  // autocomplete, recipient autocomplete. This surface only wires its own data
  // model (schema-driven ``commit(field, …)``) into that one shared UI.
  // ---------------------------------------------------------------------
  const fromValue = asString(liveValues.from_account);
  const ccCount = asStringArray(liveValues.cc).length;
  const bccCount = asStringArray(liveValues.bcc).length;
  const subjectValue = asString(liveValues.subject);

  const fromOptions = fromSpec?.options ?? [];
  const sender = resolveEmailSender(fromOptions, fromValue);
  const replyContext = Boolean(liveValues.in_reply_to_message_id || liveValues.reply_to_message_id || liveValues.forward_message_id || liveValues.thread_id);
  const activeSenderRef = React.useRef(fromValue);
  activeSenderRef.current = fromValue;
  React.useEffect(() => {
    if (sender.sendOnly && (liveValues.provider !== "gmail" || liveValues.include_signature !== false || liveValues.scheduled_for || liveValues.send_at) && onChange) onChange(changeEmailSender(liveValues, fromValue, fromOptions));
  }, [sender.sendOnly, liveValues.include_signature, fromValue, fromOptions, liveValues, onChange]);
  const showCc = ccSpec !== undefined || ccCount > 0;
  const showBcc = bccSpec !== undefined || bccCount > 0;

  // ``body_html`` rides to the BE only when THIS action's schema declares it.
  // When absent, we commit the plain-text projection alone (rich editing still
  // works) — never break the plain-text ``body`` contract. See report.
  const bodyHtmlSupported = specOfName(schema, "body_html") !== undefined;
  const bodyHtml =
    asString(liveValues.body_html) || emailBodyToHtml(asString(liveValues.body));

  // A / Cc / Ccn autocomplete — Google People + MS Graph fanout scoped to the
  // sender's CONNECTION id. The connection id is the value of the field the
  // recipients spec links via ``peopleSearchConnectionFieldName`` (conventionally
  // ``from_account``) — resolved the SAME way as the stacked ``ActionFieldEditor``
  // (single DRY contract). When the user hasn't explicitly picked a sender we
  // fall back to the FIRST offered mailbox (the primary), so typing returns hits
  // out of the box instead of the old silent-empty list (``connectionId`` was
  // always null → ``searchPeople`` short-circuited to ``[]``). Suppressed while
  // disabled; memoised so the chip debounce doesn't re-fire on every render.
  const recipientConnField =
    toSpec?.peopleSearchConnectionFieldName ??
    ccSpec?.peopleSearchConnectionFieldName ??
    bccSpec?.peopleSearchConnectionFieldName ??
    "from_account";
  const connFieldOptions = specOfName(schema, recipientConnField)?.options ?? fromOptions;
  const peopleConnectionId =
    asString(liveValues[recipientConnField]) || connFieldOptions[0]?.value || null;
  const onQueryContacts = React.useMemo<EmailContactsQuery | undefined>(() => {
    const field = toSpec ?? ccSpec ?? bccSpec;
    if (!loadPeopleOptions || disabled || !field || !sender.canAutocomplete) return undefined;
    return async (q: string) => {
      const requestedSender = fromValue;
      const hits = await loadPeopleOptions({ query: q, connectionId: peopleConnectionId, field });
      return activeSenderRef.current === requestedSender ? toChipSuggestions(hits) : [];
    };
  }, [loadPeopleOptions, disabled, peopleConnectionId, toSpec, ccSpec, bccSpec, sender.canAutocomplete, fromValue]);

  // ✨ / ⌘K body AI-edit — adapt the host's diff-mode refine transport (returns
  // ``{ original, rewritten, changes }``) to the sheet's ``aiEdit`` shape. When
  // only the legacy replace-in-place ``refineSubmit`` is wired, wrap its full
  // rewrite as a single whole-text change so the redline still renders.
  const aiEdit = React.useMemo<RichEmailAiEdit | undefined>(() => {
    if (!proposalId || disabled || bodySpec?.readonly) return undefined;
    if (refineSubmitWithDiff) {
      return async ({ text, instruction }) => {
        const r = await refineSubmitWithDiff({
          proposalId,
          fieldName: "body",
          fullText: text,
          selectionStart: 0,
          selectionEnd: text.length,
          instruction,
        });
        return { original: r.original, rewritten: r.rewritten, changes: r.changes };
      };
    }
    if (refineSubmit) {
      return async ({ text, instruction }) => {
        const r = await refineSubmit({
          proposalId,
          fieldName: "body",
          fullText: text,
          selectionStart: 0,
          selectionEnd: text.length,
          instruction,
        });
        const rewritten = r.success && r.newFullText != null ? r.newFullText : text;
        return {
          original: text,
          rewritten,
          changes:
            rewritten === text
              ? []
              : [{ id: "0", type: "replace" as const, before: text, after: rewritten }],
        };
      };
    }
    return undefined;
  }, [proposalId, disabled, bodySpec?.readonly, refineSubmitWithDiff, refineSubmit]);

  // Da — from-account picker in the sheet header (SELECT with connected
  // mailboxes, free-text otherwise). Its own border-b is dropped: the sheet's
  // header wrapper supplies the divider below the header block.
  const header = (
    <div>
      <div className="flex items-center gap-1.5 px-3 py-2">
        <span className={FIELD_LABEL}>{i18n._(msg`Da`)}</span>
        {fromOptions.length > 0 ? (
          <StyledSelect
            className={SHEET_CONTROL}
            value={fromValue}
            onChange={(e) => onChange?.(changeEmailSender(liveValues, e.target.value, fromOptions))}
            disabled={disabled || fromSpec?.readonly}
            aria-label={i18n._(msg`Mittente`)}
          >
            <option value="">{i18n._(msg`— Predefinito —`)}</option>
            {fromValue && !sender.option ? <option value={fromValue} disabled>{i18n._(msg`Mittente non disponibile`)}</option> : null}
            {fromOptions.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={replyContext && (opt.metadata?.can_reply === false || opt.metadata?.can_forward === false)}>
                {opt.label}
              </option>
            ))}
          </StyledSelect>
        ) : (
          <input
            type="email"
            className={SHEET_CONTROL}
            value={fromValue}
            onChange={(e) => onChange?.(changeEmailSender(liveValues, e.target.value, fromOptions))}
            disabled={disabled || fromSpec?.readonly}
            placeholder={i18n._(msg`andrea@example.com`)}
            aria-label={i18n._(msg`Mittente`)}
          />
        )}
      </div>
      {errors?.from_account ? (
        <p className={SHEET_ERR}>{errors.from_account.message}</p>
      ) : fromSpec?.helperText ? (
        <p className={SHEET_HELP}>{fromSpec.helperText}</p>
      ) : null}
    </div>
  );

  // Firma — dentro il foglio "sotto la mail" (Gmail), con lo STESSO footer
  // condiviso della risposta inbox: l'operatore la include/nasconde con il
  // toggle Inclusa/Nascosta. ``include_signature`` è opt-out (default TRUE); il
  // BE onora il flag all'invio (append CID inline images quando incluso).
  const includeSignature = liveValues.include_signature !== false;
  const signature = signatureHtml && !sender.sendOnly ? (
    <EmailSignatureFooter
      html={signatureHtml}
      included={includeSignature}
      onToggle={() => commit("include_signature", !includeSignature)}
      signatures={signatures}
      activeSignatureId={activeSignatureId}
      onPick={onPickSignature}
    />
  ) : undefined;

  // Allegati — the SHARED ``<EmailAttachmentField>`` (same component + pencil
  // style as the inbox reply), rendered INSIDE the sheet as a hairline band
  // above the send bar instead of a separate stacked "Allegati" section below.
  // The field NAME is schema-driven (``type: "file"`` spec, canonically
  // ``attachments``). Unlike the reply, this surface KEEPS the Ando-document
  // picker (the send-email BE resolves ``content_item_id`` chips).
  const fileSpec = schema?.find((s) => s.type === "file");
  const attachments =
    fileSpec && uploadAttachment ? (
      <EmailAttachmentField
        value={asAttachmentArray(liveValues[fileSpec.name])}
        onChange={(next) => {
          if (!values || !onChange) return;
          onChange({
            ...values,
            [fileSpec.name]: next as unknown as ActionFieldValue,
          });
        }}
        uploadAttachment={uploadAttachment}
        searchAttachableDocuments={searchAttachableDocuments}
        disabled={disabled || fileSpec.readonly}
      />
    ) : undefined;

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      <EnvelopeHeader
        icon={<Mail className="h-3.5 w-3.5" />}
        label={i18n._(msg`Bozza email`)}
      />

      <EmailComposeSheet
      key={fromValue}
        header={header}
        to={asStringArray(liveValues.to)}
        onToChange={(next) => commit("to", next as unknown as ActionFieldValue)}
        toError={errors?.to?.message}
        toHelp={toSpec?.helperText}
        toLocked={disabled || toSpec?.readonly}
        toPlaceholder={i18n._(msg`destinatario@dominio.it`)}
        cc={asStringArray(liveValues.cc)}
        onCcChange={(next) => commit("cc", next as unknown as ActionFieldValue)}
        showCc={showCc}
        ccError={errors?.cc?.message}
        ccHelp={ccSpec?.helperText}
        ccLocked={disabled || ccSpec?.readonly}
        bcc={asStringArray(liveValues.bcc)}
        onBccChange={(next) => commit("bcc", next as unknown as ActionFieldValue)}
        showBcc={showBcc}
        bccError={errors?.bcc?.message}
        bccHelp={bccSpec?.helperText}
        bccLocked={disabled || bccSpec?.readonly}
        onQueryContacts={onQueryContacts}
        subject={subjectValue}
        onSubjectChange={(v) => commit("subject", v)}
        subjectLocked={disabled || subjectSpec?.readonly}
        subjectPlaceholder={i18n._(msg`Oggetto dell'email`)}
        bodyHtml={bodyHtml}
        onBodyChange={(html, text) => {
          if (!values || !onChange) return;
          // ALWAYS commit body_html too — even when this action's schema doesn't
          // declare it. ``bodyHtml`` reads back ``liveValues.body_html`` so the
          // editor's controlled value round-trips its OWN markup; committing only
          // the plaintext ``body`` (then re-deriving HTML via ``plainToHtml``)
          // makes ``valueHtml`` diverge from ``editor.getHTML()`` on every
          // keystroke, so the controlled-sync ``setContent`` fires and jumps the
          // caret to the end (Enter looked broken). The extra field is owned +
          // ignored by the BE until the schema ships ``body_html``.
          onChange({ ...values, body: text, body_html: html });
        }}
        bodyPlaceholder={i18n._(msg`Scrivi qui il corpo dell'email…`)}
        bodyDisabled={disabled || bodySpec?.readonly}
        aiEdit={aiEdit}
        brandFonts={brandFonts}
        signature={signature}
        attachments={attachments}
      />
      {/* Subject / body validation + helper text — the shared sheet renders the
          inputs but not these action-schema messages, so they surface here
          (recipient errors/help ride inside the sheet via ``toError`` etc.). */}
      {errors?.subject ? (
        <p className={SHEET_ERR}>{errors.subject.message}</p>
      ) : subjectSpec?.helperText ? (
        <p className={SHEET_HELP}>{subjectSpec.helperText}</p>
      ) : null}
      {errors?.body ? (
        <p className={SHEET_ERR}>{errors.body.message}</p>
      ) : bodySpec?.helperText ? (
        <p className={SHEET_HELP}>{bodySpec.helperText}</p>
      ) : null}
    </div>
  );
};
EmailSendDraftPreview.displayName = "EmailSendDraftPreview";
